import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import useConfirmChanges from "components/admin/confirmChanges";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import MapMobs from "layouts/rates/MapMobs";

/** Một hàng sửa được: các ô nhập + nút lưu, chỉ bật khi có thay đổi. */
function EditRow({ fields, values, onSave, busy, extra, title }) {
  const canEdit = useCan(PERMISSIONS.RATE_EDIT);
  const ask = useConfirmChanges();
  // Tỉ lệ / quà ảnh hưởng kinh tế game: hỏi lại cũ -> mới, cảnh báo khi đổi từ 2 lần trở lên.
  const save = async () => {
    const yes = await ask({
      title,
      before: values,
      after: draft,
      fields: fields.map((f) => [f.name, f.label]),
      notes: ["Áp dụng ngay cho mọi người chơi."],
    });
    if (yes) onSave(draft);
  };
  const [draft, setDraft] = useState(values);
  const dirty = useMemo(
    () => fields.some((f) => String(draft[f.name] ?? "") !== String(values[f.name] ?? "")),
    [draft, values, fields]
  );
  return (
    <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" py={0.5}>
      {extra}
      {fields.map((f) => (
        <MDBox key={f.name} width={f.width || 90}>
          <MDTypography variant="caption" color="text" display="block" noWrap>
            {f.label}
          </MDTypography>
          <MDInput
            type="number"
            value={draft[f.name] ?? ""}
            inputProps={{ step: f.step || "any" }}
            sx={{ width: "100%" }}
            InputProps={{ readOnly: !canEdit }}
            onChange={(e) => setDraft({ ...draft, [f.name]: e.target.value })}
          />
        </MDBox>
      ))}
      {canEdit && (
        <MDButton size="small" color="info" disabled={busy || !dirty} onClick={save}>
          Lưu…
        </MDButton>
      )}
    </MDBox>
  );
}

EditRow.propTypes = {
  fields: PropTypes.array.isRequired,
  values: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  extra: PropTypes.node,
  /** Tiêu đề hộp hỏi lại, nói rõ đang sửa dòng nào. */
  title: PropTypes.string,
};
EditRow.defaultProps = { busy: false, extra: null, title: "Lưu thay đổi" };

/** Rơi đồ theo map. Từ mục 0.1 trở đi mỗi dòng roll độc lập nên percent là tỉ lệ thật. */
function Drops() {
  const [run, busy] = useRunner();
  const maps = useFetch(() => api.get("/api/admin/rates/drops"), []);

  const save = async (row, draft) => {
    const res = await run(
      () =>
        api.put("/api/admin/rates/drops", {
          mapId: row.mapId,
          dropId: row.dropId,
          itemTemplateId: row.itemTemplateId,
          percent: Number(draft.percent),
          count: Number(draft.count),
          lvlFrom: draft.lvlFrom === "" ? null : Number(draft.lvlFrom),
          lvlTo: draft.lvlTo === "" ? null : Number(draft.lvlTo),
        }),
      "Đã lưu"
    );
    if (res.ok) maps.reload();
  };

  return (
    <MDBox>
      <Alert severity="info" sx={{ mb: 2 }}>
        Mỗi dòng roll độc lập nên <strong>tỉ lệ ghi ở đây là tỉ lệ thật</strong>. &quot;Kỳ
        vọng&quot; là trung bình số món rơi ra mỗi lần giết quái trên map đó. Để trống khoảng cấp
        nghĩa là không giới hạn cấp quái.
      </Alert>
      {maps.loading && <MDTypography variant="button">Đang tải…</MDTypography>}
      <Grid container spacing={3}>
        {(maps.data || []).map((m) => (
          <Grid item xs={12} lg={6} key={m.mapId}>
            <PageCard
              title={`${m.mapName} (#${m.mapId})`}
              subtitle={`Kỳ vọng ${m.expectedPerKill} món/lần giết · tối đa ${m.maxPerKill}`}
              color="info"
            >
              <MDBox pt={2}>
                {m.items.map((row) => (
                  <EditRow
                    key={row.dropId}
                    title={`Rơi ${row.itemName} ở ${m.mapName}`}
                    busy={busy}
                    fields={[
                      { name: "percent", label: "Tỉ lệ %", width: 90 },
                      { name: "count", label: "Số lượng", width: 80, step: 1 },
                      { name: "lvlFrom", label: "Cấp từ", width: 75, step: 1 },
                      { name: "lvlTo", label: "Cấp đến", width: 75, step: 1 },
                    ]}
                    values={{
                      percent: row.percent,
                      count: row.count,
                      lvlFrom: row.lvlFrom ?? "",
                      lvlTo: row.lvlTo ?? "",
                    }}
                    extra={
                      <MDBox width={150} minWidth={150}>
                        <MDTypography variant="caption" color="text" display="block" noWrap>
                          #{row.itemTemplateId}
                        </MDTypography>
                        <MDTypography variant="button" noWrap display="block">
                          {row.itemName}
                        </MDTypography>
                      </MDBox>
                    }
                    onSave={(draft) => save(row, draft)}
                  />
                ))}
              </MDBox>
            </PageCard>
          </Grid>
        ))}
      </Grid>
    </MDBox>
  );
}

const RARITY_NAMES = ["Thường", "Khá", "Hiếm", "Cực hiếm"];

/** Quà Tiên Nữ: rút theo nhóm, cực hiếm tăng dần theo số lượt chưa ra (mục 1.5). */
function Gifts() {
  const [run, busy] = useRunner();
  const [type, setType] = useState(0);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const data = useFetch(
    () => api.get("/api/admin/rates/gifts", { type, q, page, size: 50 }),
    [type, q, page]
  );

  const save = async (row, draft) => {
    const res = await run(
      () =>
        api.put("/api/admin/rates/gifts", {
          tradeId: row.tradeId,
          rarity: Number(draft.rarity),
          slot: Number(draft.slot),
          count: Number(draft.count),
        }),
      "Đã lưu"
    );
    if (res.ok) data.reload();
  };

  const d = data.data;
  return (
    <MDBox>
      <Alert severity="info" sx={{ mb: 2 }}>
        Không dùng trọng số. Mỗi lượt: roll <strong>cực hiếm</strong> (nhóm 3) với tỉ lệ tăng dần
        theo số lượt liên tiếp chưa ra của người chơi, ra rồi thì về tỉ lệ ban đầu. Không ra thì
        chọn nhóm Thường (0) / Khá (1) / Hiếm (2) theo tỉ lệ nhóm, rồi chọn đều một món trong nhóm.
        Các dòng cùng nhóm có cùng số <strong>Món</strong> khác 0 tính là một món (vd. mọi loại mảnh
        là một món &quot;mảnh ngẫu nhiên&quot;); Món = 0 là món riêng. Tỉ lệ cực hiếm và tỉ lệ nhóm
        chỉnh ở trang &quot;Cân bằng&quot;.
      </Alert>
      {d && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Cực hiếm: bắt đầu {d.superBase}%, mỗi lượt không ra +{d.superStep}%
          {d.superGuarantee > 0 ? `, chắc chắn ra ở lượt ${d.superGuarantee}` : ""} — trung bình{" "}
          {d.meanDraws} lượt ra một lần ({d.superPerDraw}% mỗi lượt). Tỉ lệ nhóm Thường / Khá /
          Hiếm: {d.groupShare.join(" / ")}.
        </Alert>
      )}
      <MDBox display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
        <Tabs
          value={type}
          onChange={(e, v) => {
            setType(v);
            setPage(1);
          }}
        >
          <Tab label="Đổi bằng ngọc" value={0} />
          <Tab label="Đổi bằng thóc" value={2} />
          <Tab label="Đổi bằng vàng" value={1} />
        </Tabs>
        <MDInput
          label="Tìm theo tên hoặc id"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        {d && (
          <MDTypography variant="button" color="text">
            {d.rows} dòng
          </MDTypography>
        )}
      </MDBox>
      {data.loading && <MDTypography variant="button">Đang tải…</MDTypography>}
      <PageCard title="Bảng quà" color="warning">
        <MDBox pt={2}>
          {(d?.items || []).map((row) => (
            <EditRow
              key={row.tradeId}
              title={`Quà Tiên Nữ: ${row.itemName}`}
              busy={busy}
              fields={[
                { name: "rarity", label: "Nhóm (0–3)", width: 85, step: 1 },
                { name: "slot", label: "Món", width: 70, step: 1 },
                { name: "count", label: "Số lượng", width: 80, step: 1 },
              ]}
              values={{ rarity: row.rarity, slot: row.slot, count: row.count }}
              extra={
                <MDBox width={260} minWidth={260}>
                  <MDTypography variant="caption" color="text" display="block" noWrap>
                    #{row.itemTemplateId} · {RARITY_NAMES[row.rarity] ?? "?"} · tỉ lệ {row.chance}%
                  </MDTypography>
                  <MDTypography variant="button" noWrap display="block">
                    {row.itemName}
                  </MDTypography>
                </MDBox>
              }
              onSave={(draft) => save(row, draft)}
            />
          ))}
        </MDBox>
      </PageCard>
      {d && d.rows > d.size && (
        <MDBox display="flex" gap={1} mt={2} alignItems="center">
          <MDButton size="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            <Icon>chevron_left</Icon>
          </MDButton>
          <MDTypography variant="button">
            Trang {page} / {Math.ceil(d.rows / d.size)}
          </MDTypography>
          <MDButton
            size="small"
            disabled={page >= Math.ceil(d.rows / d.size)}
            onClick={() => setPage(page + 1)}
          >
            <Icon>chevron_right</Icon>
          </MDButton>
        </MDBox>
      )}
    </MDBox>
  );
}

/** Quái theo cấp: chỉ số, exp và ngọc. */
function Mobs() {
  const [run, busy] = useRunner();
  const rows = useFetch(() => api.get("/api/admin/rates/mobs"), []);

  const save = async (row, draft) => {
    const res = await run(
      () =>
        api.put("/api/admin/rates/mobs", {
          lvl: row.lvl,
          str: Number(draft.str),
          agi: Number(draft.agi),
          _int: Number(draft._int),
          hp: Number(draft.hp),
          exp: Number(draft.exp),
          coin: Number(draft.coin),
        }),
      "Đã lưu"
    );
    if (res.ok) rows.reload();
  };

  return (
    <MDBox>
      <Alert severity="info" sx={{ mb: 2 }}>
        ATK và DEF của quái suy ra từ chỉ số: <code>ATK = STR / 3 + 5</code>,{" "}
        <code>DEF = AGI / 3</code> — xem <code>docs/01-chien-dau.md</code> mục 5. Cột{" "}
        <strong>coin</strong> là ngọc nhận được khi hạ quái.
      </Alert>
      {rows.loading && <MDTypography variant="button">Đang tải…</MDTypography>}
      <PageCard title="Quái theo cấp" color="error">
        <MDBox pt={2}>
          {(rows.data || []).map((row) => (
            <EditRow
              key={row.lvl}
              title={`Quái cấp ${row.lvl}`}
              busy={busy}
              fields={[
                { name: "str", label: "STR", width: 85, step: 1 },
                { name: "agi", label: "AGI", width: 85, step: 1 },
                { name: "_int", label: "INT", width: 85, step: 1 },
                { name: "hp", label: "HP", width: 100, step: 1 },
                { name: "exp", label: "EXP", width: 90, step: 1 },
                { name: "coin", label: "Ngọc", width: 90, step: 1 },
              ]}
              values={{
                str: row.str,
                agi: row.agi,
                _int: row._int,
                hp: row.hp,
                exp: row.exp,
                coin: row.coin,
              }}
              extra={
                <MDBox width={130} minWidth={130}>
                  <MDTypography variant="button" display="block">
                    Cấp {row.lvl}
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block" noWrap>
                    ATK {row.atk} · DEF {row.def}
                  </MDTypography>
                </MDBox>
              }
              onSave={(draft) => save(row, draft)}
            />
          ))}
        </MDBox>
      </PageCard>
    </MDBox>
  );
}

/** Điểm tiềm năng mỗi cấp và điểm dung hợp của từng pet. */
function Pets() {
  const [run, busy] = useRunner();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const data = useFetch(() => api.get("/api/admin/rates/pets", { q, page, size: 50 }), [q, page]);

  const save = async (row, draft) => {
    const res = await run(
      () =>
        api.put("/api/admin/rates/pets", {
          petId: row.petId,
          gymUpLevel: Number(draft.gymUpLevel),
          fusionScore: Number(draft.fusionScore),
        }),
      "Đã lưu"
    );
    if (res.ok) data.reload();
  };

  const d = data.data;
  return (
    <MDBox>
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Tiềm năng/cấp</strong> là sàn: pet nhận{" "}
        <code>max(giá trị này, bậc đã có từ tiến hoá)</code> mỗi lần lên cấp. Cấp tối đa là 41 nên
        tổng điểm từ lên cấp bằng giá trị này × 40. Xem <code>docs/02-pet.md</code>.
      </Alert>
      <MDBox display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
        <MDInput
          label="Tìm theo tên hoặc id"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        {d && (
          <MDTypography variant="button" color="text">
            {d.total} pet
          </MDTypography>
        )}
      </MDBox>
      {data.loading && <MDTypography variant="button">Đang tải…</MDTypography>}
      <PageCard title="Pet" color="success">
        <MDBox pt={2}>
          {(d?.items || []).map((row) => (
            <EditRow
              key={row.petId}
              title={`Pet ${row.name}`}
              busy={busy}
              fields={[
                { name: "gymUpLevel", label: "Tiềm năng/cấp", width: 110, step: 1 },
                { name: "fusionScore", label: "Điểm dung hợp", width: 110, step: 1 },
              ]}
              values={{ gymUpLevel: row.gymUpLevel, fusionScore: row.fusionScore }}
              extra={
                <MDBox width={260} minWidth={260}>
                  <MDTypography variant="caption" color="text" display="block" noWrap>
                    #{row.petId} · {row.isSky ? "thiên đình" : "hạ giới"} · tổng ở cấp 41:{" "}
                    {row.totalAtLevel41}
                  </MDTypography>
                  <MDTypography variant="button" noWrap display="block">
                    {row.name}
                  </MDTypography>
                </MDBox>
              }
              onSave={(draft) => save(row, draft)}
            />
          ))}
        </MDBox>
      </PageCard>
      {d && d.total > d.size && (
        <MDBox display="flex" gap={1} mt={2} alignItems="center">
          <MDButton size="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            <Icon>chevron_left</Icon>
          </MDButton>
          <MDTypography variant="button">
            Trang {page} / {Math.ceil(d.total / d.size)}
          </MDTypography>
          <MDButton
            size="small"
            disabled={page >= Math.ceil(d.total / d.size)}
            onClick={() => setPage(page + 1)}
          >
            <Icon>chevron_right</Icon>
          </MDButton>
        </MDBox>
      )}
    </MDBox>
  );
}

function Rates() {
  // Tab ghi lên URL (?tab=0..4) để gửi link thẳng tới một bảng.
  const [params, setParams] = useSearchParams();
  const tab = [0, 1, 2, 3, 4].includes(Number(params.get("tab"))) ? Number(params.get("tab")) : 0;
  const setTab = (v) => setParams(v === 0 ? {} : { tab: String(v) }, { replace: true });
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="rates" />
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, maxWidth: 820 }}
        >
          <Tab label="Rơi đồ" />
          <Tab label="Quà Tiên Nữ" />
          <Tab label="Quái theo map" />
          <Tab label="Quái theo cấp" />
          <Tab label="Pet" />
        </Tabs>
        {tab === 0 && <Drops />}
        {tab === 1 && <Gifts />}
        {tab === 2 && <MapMobs />}
        {tab === 3 && <Mobs />}
        {tab === 4 && <Pets />}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Rates;
